import type { FeatureFlagService } from "../../../services/feature-flags";
import {
  TRACE_OVERLAY_HIDE,
  TRACE_OVERLAY_SHOW,
  buildTraceScene,
} from "./model";
import {
  msPerCharacterAt,
  pathPoints,
  phaseWalkFor,
  phaseWalkFrame,
} from "./phase-walk";
import {
  describeImageMismatch,
  matchSidecarImage,
  parseTraceSidecarText,
  sidecarMatchesImage,
} from "./sidecar";
import type {
  TraceGlyph,
  TraceAssetSelection,
  TracePaneHandle,
  TracePickerController,
  TraceScenePath,
  TraceScenePoint,
} from "./types";

/** A trace pane that does nothing, for every build where it should not. */
const INERT_PANE: TracePaneHandle = Object.freeze({
  deliveryReached: () => undefined,
  dispose: () => undefined,
});

const SVG_NAMESPACE = "http://www.w3.org/2000/svg";

function element<K extends keyof HTMLElementTagNameMap>(
  name: K,
  text?: string,
): HTMLElementTagNameMap[K] {
  const created = document.createElement(name);
  if (text !== undefined) created.textContent = text;
  return created;
}

function svgElement<K extends keyof SVGElementTagNameMap>(
  name: K,
): SVGElementTagNameMap[K] {
  return document.createElementNS(SVG_NAMESPACE, name);
}

/**
 * Ruling 67, 14 October 2026: an enemy mark is drawn as the trace draws it.
 *
 * The frame, the size amplifier above it and the role icon inside it, in red
 * — doc 03 section 7.3 fixes the colour for the frame and the icon, and the
 * amplifier is part of the same symbol. The outline is solid or dashed from
 * the mark's status.
 *
 * A mark with no glyph, or a glyph whose frame the map symbol files do not
 * define, keeps the plain circle. That is Ruling 67d, and it is why this
 * returns a list to append rather than replacing the marker: the fallback is
 * the absence of these elements, not a different drawing.
 */
function glyphElements(glyph: TraceGlyph): readonly SVGElement[] {
  const parts: SVGElement[] = [];
  const dashed = glyph.outline === "dashed";

  if (glyph.frame !== null) {
    const shape = svgElement("polygon");
    // Both are drawn about the mark's own point, at the same size as the
    // plain circle it replaces, so a trace does not jump when a sidecar
    // gains a drawing.
    shape.setAttribute(
      "points",
      glyph.frame === "triangle" ? "0,-13 12,9 -12,9" : "0,-14 14,0 0,14 -14,0",
    );
    shape.setAttribute("class", "trace-enemy-frame trace-color-red");
    shape.dataset.frame = glyph.frame;
    shape.dataset.outline = glyph.outline;
    if (dashed) shape.setAttribute("stroke-dasharray", "4 3");
    parts.push(shape);
  }

  if (glyph.icon === "armoured-reconnaissance") {
    // The armour track, and the reconnaissance diagonal across it from the
    // lower left to the upper right. Two cues, one icon.
    const track = svgElement("ellipse");
    track.setAttribute("rx", "7");
    track.setAttribute("ry", "3.5");
    track.setAttribute("class", "trace-enemy-icon trace-color-red");
    const belt = svgElement("line");
    belt.setAttribute("x1", "-7");
    belt.setAttribute("y1", "4");
    belt.setAttribute("x2", "7");
    belt.setAttribute("y2", "-4");
    belt.setAttribute("class", "trace-enemy-icon trace-color-red");
    const icon = svgElement("g");
    icon.dataset.icon = glyph.icon;
    icon.append(track, belt);
    parts.push(icon);
  }

  if (glyph.amplifierDots > 0) {
    const amplifier = svgElement("g");
    amplifier.dataset.amplifierDots = String(glyph.amplifierDots);
    const spacing = 5;
    const first = (-(glyph.amplifierDots - 1) * spacing) / 2;
    for (let index = 0; index < glyph.amplifierDots; index += 1) {
      const dot = svgElement("circle");
      dot.setAttribute("r", "1.8");
      dot.setAttribute("cx", String(first + index * spacing));
      dot.setAttribute("cy", glyph.frame === "triangle" ? "-18" : "-19");
      // Ruling 73, 14 October 2026: the amplifier is drawn as the trace
      // draws it, black. The frame and the icon stay red. In block (b)
      // this took the frame's colour on the reasoning that it is part of
      // the same symbol, and section 7.3 names only the frame and the
      // icon; the ruling settles it the other way.
      dot.setAttribute("class", "trace-enemy-amplifier trace-color-black");
      amplifier.append(dot);
    }
    parts.push(amplifier);
  }

  return parts;
}

function pointGlyph(point: TraceScenePoint): SVGGElement {
  const group = svgElement("g");
  group.dataset.pointId = point.id;
  group.dataset.kind = point.kind;
  group.dataset.type = point.type;
  group.setAttribute(
    "transform",
    `translate(${String(point.x)} ${String(point.y)})`,
  );
  const label = svgElement("text");
  label.setAttribute("x", "14");
  label.setAttribute("y", "-12");
  label.textContent = point.label;

  // Ruling 67d: anything the map symbol files do not define is drawn as
  // today's plain marker. A glyph with no frame is exactly that case, and the
  // reason is carried into the DOM so a diagnostic can read it back rather
  // than the drawing silently reverting.
  const drawn = point.glyph === null ? [] : glyphElements(point.glyph);
  if (point.glyph !== null && point.glyph.undefinedElements.length > 0) {
    group.dataset.glyphUndefined = point.glyph.undefinedElements.join("; ");
  }
  if (drawn.length === 0) {
    const marker = svgElement("circle");
    marker.setAttribute("r", point.kind === "marker" ? "10" : "7");
    marker.setAttribute("class", `trace-color-${point.color}`);
    group.append(marker, label);
    return group;
  }
  group.dataset.glyph = "true";
  group.append(...drawn, label);
  return group;
}

/**
 * An SVG laid exactly over the trace image, in the image's own 1000 grid.
 *
 * **Ruling 88**, 17 October 2026 (b) section 5, needs two of these and not
 * one: "The phase-walk pointer (Ruling 59) is shown during the walk whether
 * the overlay is on or off." The pointer was a child of the overlay, so
 * hiding the overlay hid the one thing on the trace that serves the trainee.
 */
function traceLayer(role: string, label: string): SVGSVGElement {
  const layer = svgElement("svg");
  layer.classList.add("trace-overlay");
  layer.dataset.role = role;
  layer.setAttribute("viewBox", "0 0 1000 1000");
  layer.setAttribute("preserveAspectRatio", "none");
  layer.setAttribute("aria-label", label);
  return layer;
}

/**
 * The one control Ruling 88 asks for, in the trace section.
 *
 * > "The drawn overlay is off by default. One control in the trace section,
 * > under the line 'Trace image taken from the order set.', turns it on and
 * > off. The trace image is always shown."
 *
 * It appears above the figure, which in the pre-flight band is directly under
 * that status line and in the delivery pane is directly under the Trace
 * heading. Only ever one trace section is on screen, so he sees one control.
 *
 * It touches the figure's own attribute and nothing else. There is no path
 * from here to the delivery, which is how "never pauses, stops or moves the
 * delivery" is held rather than remembered.
 */
function overlayControl(
  figure: HTMLElement,
  overlay: SVGSVGElement,
): HTMLButtonElement {
  const control = element("button", TRACE_OVERLAY_SHOW);
  control.type = "button";
  control.dataset.role = "trace-overlay-toggle";
  control.setAttribute("aria-pressed", "false");
  control.setAttribute("aria-controls", "trace-overlay-layer");
  overlay.id = "trace-overlay-layer";
  control.title =
    "The drawn marks come from the overlay file and repeat what the trace image already shows.";
  control.addEventListener("click", () => {
    const shown = figure.dataset.overlayShown !== "true";
    figure.dataset.overlayShown = String(shown);
    control.textContent = shown ? TRACE_OVERLAY_HIDE : TRACE_OVERLAY_SHOW;
    control.setAttribute("aria-pressed", String(shown));
  });
  return control;
}

function overlayFor(selection: TraceAssetSelection): SVGSVGElement | null {
  if (selection.sidecar === null) return null;
  const scene = buildTraceScene(selection.sidecar);
  const overlay = traceLayer("trace-overlay", "Trace overlay");
  for (const path of scene.paths) {
    const line = svgElement("polyline");
    line.dataset.phaseId = path.id;
    line.dataset.bindItemId = path.bindItemId;
    line.setAttribute("points", path.points);
    line.setAttribute("class", "trace-phase-path");
    overlay.append(line);
  }
  for (const point of scene.points) overlay.append(pointGlyph(point));
  return overlay;
}

/**
 * The ring that walks a phase path while its narrative is spoken.
 *
 * It is a presentation detail on purpose: every decision about where the
 * pointer is and when it has arrived is made by the pure functions in
 * `phase-walk.ts`, which are tested. This holds a circle, a clock and a
 * cancellation, and nothing that has to be reasoned about.
 *
 * Ruling 88: it is given its own layer, never the overlay's, so that turning
 * the overlay off leaves the walk visible.
 */
function pointerRing(overlay: SVGSVGElement): {
  readonly stop: () => void;
  readonly walk: (path: TraceScenePath, targetWpm: number) => void;
} {
  const ring = svgElement("circle");
  ring.dataset.role = "trace-phase-pointer";
  ring.setAttribute("r", "18");
  ring.setAttribute("class", "trace-phase-pointer");
  ring.setAttribute("visibility", "hidden");
  overlay.append(ring);

  let frame: number | null = null;
  const stop = (): void => {
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
    ring.setAttribute("visibility", "hidden");
  };

  const walk = (path: TraceScenePath, targetWpm: number): void => {
    stop();
    const points = pathPoints(path);
    if (points.length === 0) return;
    const msPerCharacter = msPerCharacterAt(targetWpm);
    const started = performance.now();
    ring.setAttribute("visibility", "visible");
    const step = (now: number): void => {
      const state = phaseWalkFrame(path, now - started, msPerCharacter);
      ring.setAttribute("cx", String(state.pointer.x));
      ring.setAttribute("cy", String(state.pointer.y));
      ring.dataset.progress = state.progress.toFixed(3);
      frame = state.complete ? null : requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
  };

  return { stop, walk };
}

export function renderTraceSelection(
  target: HTMLElement,
  selection: TraceAssetSelection,
  flags: FeatureFlagService,
  targetWpm = 0,
): TracePaneHandle {
  target.replaceChildren();
  if (!flags.isEnabled("trace")) {
    target.append(element("p", "Trace rendering is disabled in this build."));
    return INERT_PANE;
  }
  if (selection.imageUrl === null) {
    target.append(
      element(
        "p",
        "No trace image selected. Delivery will use the text-only fallback.",
      ),
    );
    return INERT_PANE;
  }

  const figure = element("figure");
  figure.className = "trace-figure";
  const image = element("img");
  image.src = selection.imageUrl;
  image.alt = "Locally selected trace";
  image.dataset.role = "trace-rendered-image";
  image.addEventListener(
    "error",
    () => {
      figure.remove();
      target.prepend(
        element(
          "p",
          "The selected trace image could not be decoded by this browser.",
        ),
      );
    },
    { once: true },
  );
  figure.append(image);
  const sidecarMatches =
    selection.sidecar === null ||
    selection.imageName === null ||
    sidecarMatchesImage(selection.sidecar.image, selection.imageName);
  const overlay = sidecarMatches ? overlayFor(selection) : null;
  // Ruling 88: the drawn overlay is off by default. The attribute is set
  // whether or not there is an overlay to show, so a diagnostic reads the
  // state rather than inferring it from an element's absence.
  figure.dataset.overlayShown = "false";
  if (overlay !== null) figure.append(overlay);
  // The pointer's own layer, outside the overlay and never hidden with it.
  const pointerLayer = traceLayer("trace-pointer-layer", "Phase walk pointer");
  figure.append(pointerLayer);
  if (overlay !== null) {
    target.append(overlayControl(figure, overlay));
  }
  target.append(figure);
  const scene =
    overlay !== null && selection.sidecar !== null
      ? buildTraceScene(selection.sidecar)
      : null;
  if (selection.sidecar === null) {
    target.append(
      element(
        "p",
        "Trace image loaded. No optional overlay selected; markers and paths are skipped.",
      ),
    );
  } else if (!sidecarMatches) {
    // Ruling 51: name both strings. "Names a different image" left the
    // trainee unable to see which of the two names was wrong.
    target.append(
      element(
        "p",
        `${describeImageMismatch(selection.sidecar.image, selection.imageName)} The overlay was not applied.`,
      ),
    );
  }

  if (overlay === null || scene === null) return INERT_PANE;

  const ring = pointerRing(pointerLayer);
  return {
    deliveryReached: (itemId) => {
      // The flag is read here, on every call, rather than once at mount. A
      // pane built before the flag was consulted must still do nothing.
      const walk = phaseWalkFor(scene, itemId, flags);
      const [first] = walk.phases;
      const showing = new Set(walk.markerIds);
      for (const glyph of overlay.querySelectorAll("g[data-point-id]")) {
        const id = (glyph as SVGGElement).dataset.pointId ?? "";
        (glyph as SVGGElement).dataset.phaseActive = String(showing.has(id));
      }
      for (const line of overlay.querySelectorAll("polyline[data-phase-id]")) {
        const bound = (line as SVGPolylineElement).dataset.bindItemId;
        (line as SVGPolylineElement).dataset.phaseActive = String(
          walk.phases.some((phase) => phase.bindItemId === bound),
        );
      }
      if (first === undefined) {
        ring.stop();
        return;
      }
      ring.walk(first, targetWpm);
    },
    dispose: ring.stop,
  };
}

export function mountTracePicker(
  target: HTMLElement,
  flags: FeatureFlagService,
): TracePickerController {
  let imageFile: File | null = null;
  let imageName: string | null = null;
  let imageUrl: string | null = null;
  let sidecar = null as TraceAssetSelection["sidecar"];

  // Shown only when no order set has supplied the image. Loading a serial
  // supplies it, so in the ordinary flow this control never appears.
  const imageLabel = element(
    "label",
    "Trace image, if no order set is loaded (.jpg, .jpeg, .png or .webp) ",
  );
  const imageInput = element("input");
  imageInput.type = "file";
  imageInput.accept = "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp";
  imageInput.dataset.role = "trace-image";
  imageLabel.append(imageInput);

  const adopted = element("p");
  adopted.dataset.role = "trace-image-adopted";
  adopted.hidden = true;

  const sidecarLabel = element("label", "Optional overlay file (.json) ");
  const sidecarInput = element("input");
  sidecarInput.type = "file";
  sidecarInput.accept = "application/json,.json";
  sidecarInput.dataset.role = "trace-sidecar";
  sidecarLabel.append(sidecarInput);

  const status = element("p");
  status.dataset.role = "trace-status";
  status.textContent = "The trace image works without an overlay file.";
  const preview = element("div");
  preview.className = "trace-preview";
  preview.dataset.role = "trace-preview";

  const selection = (): TraceAssetSelection => ({
    imageFile,
    imageName,
    imageUrl,
    sidecar,
  });
  const render = (): void => {
    renderTraceSelection(preview, selection(), flags);
  };

  imageInput.addEventListener("change", () => {
    const file = imageInput.files?.item(0);
    if (file === null || file === undefined) return;
    useImage(file, "this control");
  });

  /**
   * Read one sidecar file, from this control or from a folder.
   *
   * Ruling 71 loads a serial by its folder, and the folder holds the
   * sidecar, so the same read has two callers now. It was inline in the
   * input's handler, which would have meant the folder path either asking
   * the trainee for the sidecar again or growing a second copy of this.
   */
  const useSidecarFile = (
    file: File,
    from: "order set" | "this control",
  ): void => {
    status.textContent =
      from === "order set"
        ? "Reading the overlay from the folder..."
        : "Reading local optional overlay...";
    void file
      .text()
      .then((serialized) => {
        const parsed = parseTraceSidecarText(serialized);
        if (!parsed.ok) {
          sidecar = null;
          status.textContent = parsed.error.message;
          status.dataset.status = "error";
          render();
          return;
        }
        sidecar = parsed.value;
        const match =
          imageName === null
            ? "exact"
            : matchSidecarImage(sidecar.image, imageName);
        status.textContent =
          match === "exact"
            ? "Optional overlay loaded locally."
            : match === "normalized"
              ? // Ruling 51: it matched, and he should know the two names
                // differ, because one of them is probably wrong.
                `Optional overlay loaded. Its image name and the loaded image differ only in spaces, underscores or hyphens: "${sidecar.image}" against "${imageName ?? ""}".`
              : describeImageMismatch(sidecar.image, imageName ?? "");
        status.dataset.status = match === "different" ? "error" : "ready";
        render();
      })
      .catch(() => {
        sidecar = null;
        status.textContent = "The local optional overlay could not be read.";
        status.dataset.status = "error";
        render();
      });
  };

  sidecarInput.addEventListener("change", () => {
    const file = sidecarInput.files?.item(0);
    if (file === null || file === undefined) return;
    useSidecarFile(file, "this control");
  });

  const useImage = (file: File, from: "order set" | "this control"): void => {
    if (imageUrl !== null) URL.revokeObjectURL(imageUrl);
    imageFile = file;
    imageName = file.name;
    imageUrl = URL.createObjectURL(file);
    const mismatched =
      sidecar !== null && !sidecarMatchesImage(sidecar.image, file.name);
    status.textContent = mismatched
      ? describeImageMismatch(sidecar?.image ?? "", file.name)
      : from === "order set"
        ? "Trace image taken from the order set."
        : "Trace image loaded locally.";
    status.dataset.status = mismatched ? "error" : "ready";
    render();
  };

  target.append(imageLabel, adopted, sidecarLabel, status, preview);
  render();
  return {
    adoptImage: (file) => {
      if (file === null) {
        imageLabel.hidden = false;
        adopted.hidden = true;
        return;
      }
      // The order set has the image, so this band must not ask for it again.
      imageLabel.hidden = true;
      adopted.hidden = false;
      adopted.textContent = `Trace image: ${file.name}, taken from the order set. It is not asked for again.`;
      useImage(file, "order set");
    },
    adoptSidecar: (file) => {
      if (file === null) return;
      // The folder has the sidecar, so this band must not ask for it again.
      sidecarLabel.hidden = true;
      useSidecarFile(file, "order set");
    },
    dispose: () => {
      if (imageUrl !== null) URL.revokeObjectURL(imageUrl);
      imageUrl = null;
      imageFile = null;
      imageName = null;
      sidecar = null;
    },
    selection,
  };
}
